/**
 * Premium sidebar shell shared by finops / studio / id product apps.
 * Dark navy layered surfaces, beacon brand header with trust signals,
 * ⌘K navigation palette, section accent dots and glass active states.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, LogOut, CornerDownLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import clsx from 'clsx';
import { Logo } from './Logo';

export type SidebarAccent = 'blue' | 'cyan' | 'emerald' | 'amber' | 'violet';
export type SidebarNavItem = { path: string; icon: any; label: string; badge?: string; badgeTone?: 'ok' | 'warn' | 'default' };
export type SidebarNavSection = { title: string; accent: SidebarAccent; items: SidebarNavItem[] };
export type TrustSignal = { label: string; kind: 'live' | 'chip' | 'plain' };
export type SidebarFooterLink = { href: string; icon: any; label: string; testid: string };

const ACCENT: Record<SidebarAccent, { dot: string; label: string }> = {
  blue:    { dot: 'bg-blue-500 shadow-[0_0_10px_2px_rgba(59,130,246,.5)]',    label: 'text-blue-300/80' },
  cyan:    { dot: 'bg-sky-400 shadow-[0_0_10px_2px_rgba(56,189,248,.5)]',     label: 'text-sky-300/80' },
  emerald: { dot: 'bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,.5)]', label: 'text-emerald-300/80' },
  amber:   { dot: 'bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,.5)]',   label: 'text-amber-300/80' },
  violet:  { dot: 'bg-violet-400 shadow-[0_0_10px_2px_rgba(167,139,250,.5)]', label: 'text-violet-300/80' },
};

const BEACON: Record<string, string> = {
  finops: 'conic-gradient(from 210deg at 50% 50%, #1D4ED8, #3B82F6, #0EA5E9, #22D3EE, #34D399, #3B82F6, #1D4ED8)',
  studio: 'conic-gradient(from 210deg at 50% 50%, #6D28D9, #8B5CF6, #A855F7, #E879F9, #38BDF8, #8B5CF6, #6D28D9)',
  id:     'conic-gradient(from 210deg at 50% 50%, #1E40AF, #3B82F6, #60A5FA, #22D3EE, #3B82F6, #1E40AF)',
};

export interface SidebarShellProps {
  product: 'finops' | 'studio' | 'id';
  title: string;
  subtitle: string;
  trust: TrustSignal[];
  sections: SidebarNavSection[];
  metrics?: React.ReactNode;
  user?: { email?: string; role?: string };
  planLabel?: string;
  testidPrefix: string;
  rootKey?: string;
  rootPath?: string;
  currentPath: string;
  LinkComponent: any;
  onNavigate: (path: string) => void;
  footerLinks?: SidebarFooterLink[];
  onSignOut: () => void;
  children: React.ReactNode;
}

export function SidebarShell(props: SidebarShellProps) {
  const {
    product, title, subtitle, trust, sections, metrics, user, planLabel,
    testidPrefix, rootKey = 'overview', rootPath = '/', currentPath,
    LinkComponent: L, onNavigate, footerLinks, onSignOut, children,
  } = props;
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('cg-sidebar-collapsed') === '1'; } catch { return false; }
  });

  function toggleCollapsed() {
    setCollapsed((v) => {
      try { localStorage.setItem('cg-sidebar-collapsed', v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((v) => !v); }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function testIdFor(path: string) {
    return `${testidPrefix}-nav-${path.split('/').filter(Boolean).join('-') || rootKey}`;
  }

  function renderItem(item: SidebarNavItem) {
    const active = currentPath === item.path || (item.path !== rootPath && currentPath.startsWith(item.path + '/'));
    const Icon = item.icon;
    return (
      <L
        key={item.path}
        to={item.path}
        title={collapsed ? item.label : undefined}
        data-testid={testIdFor(item.path)}
        className={clsx(
          'flex items-center rounded-lg text-[13px] transition-colors duration-150',
          collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-3 py-[7px]',
          active
            ? 'bg-secondary/90 text-foreground font-medium shadow-[inset_0_0_0_1px_rgba(96,165,250,.22),inset_0_1px_0_rgba(255,255,255,.07),0_0_18px_rgba(59,130,246,.12)]'
            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
        )}
      >
        <Icon className={clsx('h-4 w-4 shrink-0', active && 'text-blue-400')} />
        {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        {!collapsed && item.badge && (
          <span
            className={clsx(
              'text-[9px] font-semibold rounded-full border px-1.5 py-px',
              item.badgeTone === 'ok' && 'text-emerald-300 border-emerald-500/25 bg-emerald-500/10',
              item.badgeTone === 'warn' && 'text-amber-300 border-amber-500/25 bg-amber-500/10',
              (!item.badgeTone || item.badgeTone === 'default') && 'text-muted-foreground border-border bg-muted/40',
            )}
          >
            {item.badge}
          </span>
        )}
      </L>
    );
  }

  const initials = (user?.email || '??').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        className={clsx(
          'shrink-0 sticky top-0 h-screen flex flex-col border-r border-border bg-sidebar relative transition-[width] duration-200',
          collapsed ? 'w-[70px]' : 'w-72',
        )}
        data-testid={`${testidPrefix}-sidebar`}
      >
        <div
          className="pointer-events-none absolute -top-16 -left-10 w-[300px] h-[220px] cg-breathe"
          style={{ background: 'radial-gradient(closest-side, rgba(56,189,248,.12), transparent 70%)' }}
        />

        {/* Brand header */}
        <div className={clsx('relative', collapsed ? 'px-2 pt-4 pb-2' : 'px-5 pt-5 pb-4')}>
          {!collapsed && (
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Collapse sidebar"
              data-testid={`${testidPrefix}-sidebar-toggle`}
              className="absolute top-4 right-3 z-10 p-1 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-accent/60 transition-colors duration-150"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
          <L to={rootPath} className={clsx('flex items-center group', collapsed ? 'justify-center' : 'gap-3.5')}>
            <span className={clsx('relative shrink-0', collapsed ? 'w-[38px] h-[38px]' : 'w-[46px] h-[46px]')}>
              <span className="absolute -inset-1.5 rounded-2xl border border-blue-400/25 cg-breathe"
                style={{ background: 'radial-gradient(closest-side, rgba(59,130,246,.14), transparent 75%)' }} />
              <span
                className="absolute inset-0 rounded-xl flex items-center justify-center overflow-hidden"
                style={{
                  background: BEACON[product],
                  boxShadow: '0 5px 22px rgba(37,99,235,.45), inset 0 2px 3px rgba(255,255,255,.35), inset 0 -5px 12px rgba(2,6,23,.45)',
                }}
              >
                <span className="absolute inset-0.5 rounded-[10px]" style={{ background: 'linear-gradient(160deg, rgba(255,255,255,.24), transparent 42%)' }} />
                <Logo product={product} size={collapsed ? 22 : 26} className="relative z-[1] drop-shadow-[0_2px_4px_rgba(2,6,23,.6)]" />
              </span>
            </span>
            {!collapsed && (
              <span className="min-w-0">
                <span className="block text-[17px] font-extrabold tracking-tight leading-tight">{title}</span>
                <span className="block text-[10px] font-medium text-sky-200/70 mt-0.5">{subtitle}</span>
              </span>
            )}
          </L>

          {collapsed && (
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Expand sidebar"
              data-testid={`${testidPrefix}-sidebar-toggle`}
              className="mt-2.5 mx-auto flex items-center justify-center w-full py-1.5 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-accent/60 transition-colors duration-150"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}

          {!collapsed && (
            <div className="flex items-center flex-wrap gap-1.5 mt-3.5" data-testid={`${testidPrefix}-trust-row`}>
              {trust.map((t) =>
              t.kind === 'live' ? (
                <span key={t.label} className="flex items-center gap-1.5 text-[8.5px] font-bold tracking-[.1em] uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2.5 py-[3px]">
                  <span className="w-[5px] h-[5px] rounded-full bg-emerald-400 shadow-[0_0_7px_rgba(52,211,153,.9)] cg-pulse-dot" />
                  {t.label}
                </span>
              ) : t.kind === 'chip' ? (
                <span key={t.label} className="text-[8.5px] font-mono font-bold tracking-[.06em] text-sky-300 bg-sky-500/10 border border-sky-500/25 rounded-full px-2.5 py-[3px]">
                  {t.label}
                </span>
              ) : (
                <span key={t.label} className="text-[9px] font-semibold tracking-[.12em] text-muted-foreground/80">{t.label}</span>
              ),
              )}
            </div>
          )}
          <div className={clsx('h-px', collapsed ? 'mt-2' : 'mt-3.5')} style={{ background: 'linear-gradient(90deg, rgba(56,189,248,.4), rgba(52,211,153,.18), transparent)' }} />
        </div>

        {/* Command palette trigger */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          title={collapsed ? 'Search pages (⌘K)' : undefined}
          data-testid={`${testidPrefix}-cmdk-trigger`}
          className={clsx(
            'flex items-center rounded-[10px] border border-border bg-muted/30 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors duration-150 shadow-[inset_0_1px_0_rgba(255,255,255,.05)]',
            collapsed ? 'mx-2 mb-2 justify-center py-2' : 'mx-4 mb-2 gap-2.5 px-3 py-2',
          )}
        >
          <Search className="h-3.5 w-3.5" />
          {!collapsed && <span className="flex-1 text-left">Search pages…</span>}
          {!collapsed && <kbd className="font-mono text-[9px] font-semibold text-muted-foreground bg-muted/60 border border-border border-b-2 rounded px-1.5 py-px">⌘K</kbd>}
        </button>

        {!collapsed && metrics && <div className="mx-4 mb-1">{metrics}</div>}

        {/* Nav */}
        <nav className={clsx('flex-1 overflow-y-auto pb-2 pt-1', collapsed ? 'px-2' : 'px-4')}>
          {sections.map((s) => (
            <section key={s.title}>
              {collapsed ? (
                <div className="flex justify-center pt-4 pb-1" title={s.title}>
                  <span className={clsx('w-2 h-2 rounded-full shrink-0', ACCENT[s.accent].dot)} />
                </div>
              ) : (
                <div className={clsx('flex items-center gap-2 px-2.5 pt-5 pb-1.5 text-[9.5px] font-bold uppercase tracking-[.14em]', ACCENT[s.accent].label)}>
                  <span className={clsx('w-2 h-2 rounded-full shrink-0', ACCENT[s.accent].dot)} />
                  {s.title}
                </div>
              )}
              <div className="space-y-0.5">{s.items.map(renderItem)}</div>
            </section>
          ))}
          {footerLinks && footerLinks.length > 0 && (
            <section className="mt-5 pt-4 border-t border-border/60 space-y-0.5">
              {footerLinks.map((f) => {
                const Icon = f.icon;
                return (
                  <a key={f.testid} href={f.href} data-testid={f.testid} title={collapsed ? f.label : undefined}
                    className={clsx(
                      'flex items-center rounded-lg text-[13px] text-muted-foreground hover:bg-accent/60 hover:text-foreground transition-colors duration-150',
                      collapsed ? 'justify-center py-2' : 'gap-2.5 px-3 py-[7px]',
                    )}>
                    <Icon className="h-4 w-4 shrink-0" /> {!collapsed && f.label}
                  </a>
                );
              })}
            </section>
          )}
        </nav>

        {/* User footer */}
        <div className={clsx(
          'mb-4 mt-2 flex items-center rounded-xl border border-border bg-card shadow-[inset_0_1px_0_rgba(255,255,255,.05)]',
          collapsed ? 'mx-2 flex-col gap-1.5 px-1.5 py-2' : 'mx-4 gap-2.5 px-3 py-2.5',
        )}>
          <span className="w-8 h-8 rounded-lg border border-border bg-elev flex items-center justify-center text-[10.5px] font-bold text-foreground/80" title={collapsed ? user?.email : undefined}>{initials}</span>
          <span className={clsx('flex-1 min-w-0', collapsed && 'hidden')}>
            <span className="block text-[11.5px] font-medium truncate" data-testid={`${testidPrefix}-user-email`}>{user?.email}</span>
            <span className="block text-[9.5px] text-muted-foreground">{user?.role || '—'}{planLabel ? ` · ${planLabel}` : ''}</span>
          </span>
          <button type="button" onClick={onSignOut} data-testid={`${testidPrefix}-logout`} title="Sign out"
            className="text-muted-foreground hover:text-foreground transition-colors duration-150 p-1">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden" data-testid={`${testidPrefix}-main`}>
        {children}
      </main>

      {paletteOpen && (
        <CommandPalette
          sections={sections}
          testidPrefix={testidPrefix}
          onClose={() => setPaletteOpen(false)}
          onNavigate={(p) => { setPaletteOpen(false); onNavigate(p); }}
        />
      )}
    </div>
  );
}

function CommandPalette({ sections, testidPrefix, onClose, onNavigate }: {
  sections: SidebarNavSection[];
  testidPrefix: string;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const [q, setQ] = useState('');
  const flat = useMemo(
    () => sections.flatMap((s) => s.items.map((i) => ({ ...i, section: s.title }))),
    [sections],
  );
  const results = q.trim()
    ? flat.filter((i) => i.label.toLowerCase().includes(q.toLowerCase()) || i.section.toLowerCase().includes(q.toLowerCase()))
    : flat;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[14vh] px-4"
      onClick={onClose}
      data-testid={`${testidPrefix}-cmdk`}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) onNavigate(results[0].path); }}
            placeholder="Jump to a page…"
            data-testid={`${testidPrefix}-cmdk-input`}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
          <kbd className="font-mono text-[9px] text-muted-foreground border border-border rounded px-1.5 py-px">esc</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-1.5">
          {results.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">No matches</div>}
          {results.slice(0, 30).map((i, idx) => {
            const Icon = i.icon;
            return (
              <button
                key={i.path}
                type="button"
                onClick={() => onNavigate(i.path)}
                data-testid={`${testidPrefix}-cmdk-item-${i.path.split('/').filter(Boolean).join('-') || 'root'}`}
                className={clsx(
                  'w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors duration-100',
                  idx === 0 && q.trim() ? 'bg-accent/60 text-foreground' : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{i.label}</span>
                <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground/70">{i.section}</span>
                {idx === 0 && q.trim() && <CornerDownLeft className="h-3 w-3" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SidebarMetrics({ title, delta, deltaTone = 'ok', value, valueSuffix, split, testid }: {
  title: string;
  delta?: string;
  deltaTone?: 'ok' | 'warn';
  value: string;
  valueSuffix?: string;
  split?: { label: string; value: string; tone?: 'ok' | 'warn' | 'default' }[];
  testid?: string;
}) {
  return (
    <div
      className="rounded-xl border border-blue-400/15 px-3.5 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,.06),0_6px_20px_rgba(0,0,0,.3)]"
      style={{ background: 'linear-gradient(140deg, hsl(var(--cg-elev)), hsl(var(--cg-card)))' }}
      data-testid={testid}
    >
      <div className="flex justify-between items-baseline">
        <span className="text-[9px] font-bold uppercase tracking-[.12em] text-muted-foreground">{title}</span>
        {delta && <span className={clsx('text-[10px] font-semibold', deltaTone === 'ok' ? 'text-emerald-400' : 'text-amber-400')}>{delta}</span>}
      </div>
      <div className="mt-1 text-xl font-bold tracking-tight">
        {value}
        {valueSuffix && <span className="text-[10.5px] font-medium text-muted-foreground ml-1">{valueSuffix}</span>}
      </div>
      {split && split.length > 0 && (
        <div className="flex gap-4 mt-2.5 pt-2.5 border-t border-border/70">
          {split.map((s) => (
            <div key={s.label}>
              <span className="block text-[8.5px] font-bold uppercase tracking-[.08em] text-muted-foreground/80">{s.label}</span>
              <span className={clsx(
                'text-xs font-semibold',
                s.tone === 'ok' && 'text-emerald-400',
                s.tone === 'warn' && 'text-amber-400',
                (!s.tone || s.tone === 'default') && 'text-foreground/85',
              )}>{s.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SidebarShell;
